import React, { useEffect, useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { omit } from "lodash";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Button,
  Checkbox,
  CircularProgress,
  Grid,
  TextField,
} from "@mui/material";

import { regexForName } from "common/constant";
import {
  addressOptions,
  getOptionLabel,
  salutationOptions,
} from "common/enums";
import { AuthorizedInfoWrapper } from "Components/AuthorizedPersonDetails/style";
import SelectField from "Components/Common/SelectField";
import InputFieldText from "Components/Common/InputFieldText";
import Modal from "Components/Common/Modal";
import Backdrop from "Components/Common/Backdrop";
import ConfirmationDialog from "Components/Common/ConfirmationDialog";

import { filterEmptyStrings, getImageUrl, onlyNumberKeys } from "utils";
import debounceFn from "utils/debounce";

import {
  useLazyGetAuthorizedUserQuery,
  usePostAuthorizedPersonMutation,
  useVerifyUserConsumerDetailsMutation,
} from "apis/User";
import { useLazyLocationByPincodeQuery } from "apis/Location";
import { useLazyGetUtilityConsumerQuery } from "apis/UtilityConsumer/utilityConsumer";
import VerifiedConsumerModal from "./VerifiedConsumerModal";


const validationSchema = {
  salutation: Yup.string().required("Please select your salutation").nullable(),
  firstName: Yup.string()
    .matches(regexForName, "Please enter valid first name")
    .required("Please enter your first name"),
  middleName: Yup.string().matches(
    regexForName,
    "Please enter valid middle name"
  ),
  lastName: Yup.string().matches(regexForName, "Please enter valid last name"),
  addressType: Yup.string().required("Please Select Address Type "),
  mobileNumber: Yup.string()
    .required("Mobile number is required")
    .matches(/^\d{10}$/, "Please enter valid mobile number"),
  addressLine1: Yup.string().required("Please enter your address"),
  city: Yup.object({
    id: Yup.number().required("City is required"),
  }),
  pinCode: Yup.string()
    .matches(/^\d{6}$/, "Please enter valid pincode")
    .required("Please enter your pincode"),
  email: Yup.string()
    .email("Please enter valid email")
    .required("Please enter your email"),
};

const initialQuery = {
  pageSize: 10,
  pageNumber: 1,
  consumerNumber: "",
};

const AddOrEditAuthorizedPerson = ({
  authorizedPersonUserId,
  handleClose,
  t,
}) => {
  const [
    getAuthorizedPersonDetails,
    {
      data: { data: authorizedPerson } = {},
      isLoading: isAuthorizedPersonDetailsLoading,
    },
  ] = useLazyGetAuthorizedUserQuery();

  const [
    getAllConsumerNumbers,
    {
      data: { data: { items: allConsumerNumbers } = [] } = {},
      isFetching: isConsumerNumbersFetching,
      isLoading: isConsumerNumbersLoading,
    },
  ] = useLazyGetUtilityConsumerQuery();

  const [addAuthorizedUser, { isLoading: isAddEditUserLoading }] =
    usePostAuthorizedPersonMutation();

  const [verifyConsumer, { isLoading: isConsumerVerifying }] =
    useVerifyUserConsumerDetailsMutation();

  const [verifiedConsumerData, setVerifiedConsumerData] = useState(null);

  const [getLocation] = useLazyLocationByPincodeQuery();

  const [query, setQuery] = useState(initialQuery);
  const [pinCodeError, setPinCodeError] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [
    isConfirmationModalForChangeUserDetailOpen,
    setIsConfirmationModalForChangeUserDetailOpen,
  ] = useState(false);

  const updatedConsumerNumberOptions = useMemo(
    () =>
      allConsumerNumbers?.map(({ consumerNumber, id, utility }) => ({
        label: `${consumerNumber} : ${
          getOptionLabel("utilityOptions", utility) ?? ""
        }`,
        value: id,
        utility,
        consumerNumber,
      })) ?? [],
    [allConsumerNumbers]
  );

  const initialValues = useMemo(
    () => ({
      id: authorizedPerson?.id ?? "",
      salutation: authorizedPerson?.salutation ?? "",
      firstName: authorizedPerson?.firstName ?? "",
      middleName: authorizedPerson?.middleName ?? "",
      lastName: authorizedPerson?.lastName ?? "",
      designation: authorizedPerson?.designation ?? "",
      email: authorizedPerson?.email ?? "",
      mobileNumber: authorizedPerson?.mobileNumber ?? "",
      addressType: authorizedPerson?.addressType ?? "",
      addressLine1: authorizedPerson?.addressLine1 ?? "",
      addressLine2: authorizedPerson?.addressLine2 ?? "",
      city: authorizedPerson?.city,
      pinCode: authorizedPerson?.pinCode ?? "",
      profileImageURL: getImageUrl(authorizedPerson?.profileImage) ?? "",
      file: "",
      consumerNumbers:
        authorizedPerson?.authorizedUserConsumers?.map(
          ({ consumerNumber, id, utilityId }) => ({
            label: `${consumerNumber} : ${
              getOptionLabel("utilityOptions", utilityId) ?? ""
            }`,
            value: id,
            utilityId,
            consumerNumber,
          })
        ) ?? [],
    }),
    [authorizedPerson]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: Yup.object({
      ...validationSchema,
      ...(!authorizedPersonUserId && {
        consumerNumbers: Yup.array().min(1, "Please select consumer number"),
      }),
    }),
    onSubmit: async (values) => {
      try {
        const verifyConsumerData = values.consumerNumbers.map(
          ({ value }) => value
        );

        const { data: consumerVerifiedData } = await verifyConsumer(
          verifyConsumerData
        );

        if (consumerVerifiedData?.length) {
          setVerifiedConsumerData(consumerVerifiedData);
        }
      } catch (error) {
        console.error(error);
      }
    },
  });

  const {
    setFieldValue,
    values,
    values: {  pinCode, consumerNumbers },
    handleSubmit,
    touched,
    errors,
  } = formik;

  const handleInputChange = (_, newInputValue) => {
    setInputValue(newInputValue);
    debounceFn(
      () =>
        getAllConsumerNumbers({
          ...query,
          consumerNumber: newInputValue,
        }),
      300
    );
  };

  const handleScroll = (event) => {
    const { target } = event;
    const { scrollTop, scrollHeight, clientHeight } = target;

    if (scrollTop + clientHeight >= scrollHeight) {
      setQuery((prev) => ({
        ...prev,
        pageSize: prev.pageSize + 10,
        consumerNumber: inputValue,
      }));
    }
  };

  const handleAuthorizedConsumerSubmission = async () => {
    try {
      const payload = filterEmptyStrings({
        ...omit(values, ["city", "profileImageURL", "consumerNumbers"]),
        cityId: values.city?.id ?? "",
      });

      await addAuthorizedUser({
        ...payload,
        mobileNumber: values.mobileNumber.toString(),
        utilityConsumerIds: values.consumerNumbers.map(({ value }) => value),
      });
      handleClose();
    } catch (error) {}
  };

  useEffect(() => {
    if (!authorizedPersonUserId) {
      getAllConsumerNumbers(query);
    }
  }, [query]);

  useEffect(() => {
    if (authorizedPersonUserId) {
      getAuthorizedPersonDetails(authorizedPersonUserId);
    }
  }, [authorizedPersonUserId]);

  useEffect(() => {
    (async () => {
      if (pinCode.toString().length === 6) {
        try {
          const res = await getLocation(pinCode);
          setPinCodeError("");
          setFieldValue("city", res.data.city);
        } catch (error) {
          setPinCodeError(t("Please enter valid pincode"));
          setFieldValue("city", "");
        }
      }
    })();
  }, [pinCode, getLocation, setFieldValue]);

  if (isAuthorizedPersonDetailsLoading) {
    return <Backdrop open />;
  }

  return (
    <>
      <ConfirmationDialog
        isOpen={isConfirmationModalForChangeUserDetailOpen}
        title="Warning"
        message="Updating these user details will also affect the records for other utility consumers user details. Do you want to proceed?"
        onCancel={() => setIsConfirmationModalForChangeUserDetailOpen(false)}
        onConfirm={handleSubmit}
        isLoading={isAddEditUserLoading}
        variant="warning"
      />

      {!!verifiedConsumerData?.length && (
        <Modal
          isOpen
          handleClose={() => setVerifiedConsumerData(null)}
          title={"User Details"}
          maxWidth="md"
        >
          <p className="mt-3" style={{ color: "red" }}>
            Upon submission, the designated authorized person for the consumers
            listed below will be permanently revised.
          </p>
          <VerifiedConsumerModal rowData={verifiedConsumerData} />
          <Button
            className="float-end mt-4"
            variant="contained"
            onClick={handleAuthorizedConsumerSubmission}
          >
            {isAddEditUserLoading && <CircularProgress size={20} />}
            {t("Submit")}
          </Button>
        </Modal>
      )}

      <Modal
        isOpen
        handleClose={handleClose}
        title={`${authorizedPersonUserId ? "Edit" : "Add"} User Details`}
        maxWidth="md"
      >
        <Grid container className="mt-3">
          <AuthorizedInfoWrapper>
            <Grid container spacing={2}>
              <Grid item sm={12}>
                <Grid container item columnSpacing={2}>
                  <Grid item xs={12} sm={6} lg={4}>
                    <SelectField
                      label={t("Salutation")}
                      name="salutation"
                      formik={formik}
                      options={salutationOptions}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={4}>
                    <InputFieldText
                      label={t("First Name")}
                      name="firstName"
                      formik={formik}
                      placeholder="Enter First Name"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={4}>
                    <InputFieldText
                      label={t("Middle Name")}
                      name="middleName"
                      formik={formik}
                      placeholder="Enter Middle Name"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={4}>
                    <InputFieldText
                      label={t("Last Name")}
                      name="lastName"
                      formik={formik}
                      placeholder="Enter Last Name"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={4}>
                    <InputFieldText
                      label={t("Designation")}
                      name="designation"
                      formik={formik}
                      placeholder="Enter Designation"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={4}>
                    <InputFieldText
                      label={t("Email")}
                      name="email"
                      formik={formik}
                      type="email"
                      placeholder="Enter Email"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={4}>
                    <InputFieldText
                      label={t("Mobile Number")}
                      name="mobileNumber"
                      formik={formik}
                      type="number"
                      InputProps={{
                        inputProps: { min: 0, onKeyDown: onlyNumberKeys },
                      }}
                      placeholder="Enter Mobile Number"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={4}>
                    <SelectField
                      label={t("Address Type")}
                      name="addressType"
                      formik={formik}
                      options={addressOptions}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={4}>
                    <InputFieldText
                      label={t("Address Line 1")}
                      name="addressLine1"
                      formik={formik}
                      placeholder="Enter Address"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={4}>
                    <InputFieldText
                      label={t("Address Line 2")}
                      name="addressLine2"
                      formik={formik}
                      placeholder="Enter Address"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={4}>
                    <InputFieldText
                      label={t("City")}
                      name="city.name"
                      formik={formik}
                      placeholder="City"
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={4}>
                    <InputFieldText
                      label={t("Pincode")}
                      name="pinCode"
                      formik={formik}
                      errorMessage={pinCodeError}
                      type="number"
                      InputProps={{
                        inputProps: { min: 0, onKeyDown: onlyNumberKeys },
                      }}
                      placeholder="Enter Pincode"
                    />
                  </Grid>

                  <Grid item xs={12} sm={12} lg={12}>
                    <SelectField
                      label={t("Consumer Numbers")}
                      name="consumerNumbers"
                      formik={formik}
                      options={updatedConsumerNumberOptions}
                      onInputChange={handleInputChange}
                      multiple
                      onChange={(_, options, type) => {
                        const currentSelectedOption =
                          options[options.length - 1];
                        if (type === "removeOption") {
                          const availableAuthorizedUserIds =
                            authorizedPerson?.authorizedUserConsumers?.map(
                              ({ id }) => id
                            );
                          const currentAuthorizedUserIds = options.map(
                            ({ value }) => value
                          );
                          const isConsumerCanBeRemoved =
                            availableAuthorizedUserIds?.every((id) =>
                              currentAuthorizedUserIds.includes(id)
                            );
                          if (isConsumerCanBeRemoved || !authorizedPersonUserId)
                            setFieldValue("consumerNumbers", options);
                        } else if (
                          consumerNumbers.some(
                            ({ value }) => value === currentSelectedOption.value
                          )
                        ) {
                          setFieldValue(
                            "consumerNumbers",
                            consumerNumbers.filter(
                              ({ value }) =>
                                value !== currentSelectedOption.value
                            )
                          );
                        } else {
                          setFieldValue("consumerNumbers", options);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t("Consumer Numbers")}
                          error={
                            !!errors.consumerNumbers &&
                            !!touched.consumerNumbers
                          }
                          variant="outlined"
                          sx={{
                            position: "relative",
                          }}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {isConsumerNumbersLoading ||
                                isConsumerNumbersFetching ? (
                                  <CircularProgress
                                    size={25}
                                    sx={{
                                      position: "absolute",
                                      right: "10px",
                                    }}
                                  />
                                ) : (
                                  params.InputProps.endAdornment
                                )}
                              </>
                            ),
                          }}
                          type="text"
                        />
                      )}
                      disableCloseOnSelect
                      renderOption={(props, option) => {
                        return (
                          <li {...props} key={option.value}>
                            <Checkbox
                              sx={{ marginRight: "8px" }}
                              checked={consumerNumbers.some(
                                ({ value }) => value === option.value
                              )}
                            />
                            {option.label}
                          </li>
                        );
                      }}
                      ListboxProps={{
                        onScroll: handleScroll,
                      }}
                      onClose={() => setQuery(initialQuery)}
                      value={consumerNumbers}
                      loading={
                        isConsumerNumbersLoading || isConsumerNumbersFetching
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </AuthorizedInfoWrapper>
        </Grid>
        <Button
          className="float-end"
          variant="contained"
          onClick={() => {
            authorizedPerson?.isUserAssignedToOtherUtilityConsumers
              ? setIsConfirmationModalForChangeUserDetailOpen(true)
              : handleSubmit();
          }}
        >
          {(isAddEditUserLoading || isConsumerVerifying) && (
            <CircularProgress size={20} />
          )}
          {t("Submit")}
        </Button>
      </Modal>
    </>
  );
};

AddOrEditAuthorizedPerson.propTypes = {
  authorizedPersonUserId: PropTypes.number,
  handleClose: PropTypes.func,
  t: PropTypes.func,
};

export default withTranslation()(AddOrEditAuthorizedPerson);
